import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ContributorToken, DataSourceLoadOptions, AdditionalFiltering } from '@sleed/bizcommerce-api';
import { contributorsApi, binaryRepoImagesApi } from '~/lib/api/client';
import type { SortDir } from '~/lib/grid/sort';
import { formToToken, type ContributorFormValues } from '~/lib/schemas/contributor';

/** Server-side sort selectors the ContributorsController accepts (_AllowedFilterMappings). */
export type ContributorSortKey = 'fn' | 'ln' | 'id';

export interface ContributorsListParams {
	skip?: number;
	take?: number;
	sortSelector?: ContributorSortKey | null;
	sortDir?: SortDir;
	filters?: AdditionalFiltering;
}

export interface ContributorsListResult {
	items: ContributorToken[];
	totalCount: number;
}

export const contributorKeys = {
	all: ['contributors'] as const,
	list: (params: unknown) => ['contributors', 'list', params] as const,
	detail: (id: number) => ['contributors', 'detail', id] as const
};

/** Server-paged contributors list via `getAll` (DevExtreme loadOptions + additionalFiltering envelope). */
export function useContributorsList(params: ContributorsListParams = {}) {
	const { skip = 0, take = 25, sortSelector = 'ln', sortDir = 'asc', filters } = params;
	return useQuery<ContributorsListResult>({
		queryKey: contributorKeys.list({ skip, take, sortSelector, sortDir, filters }),
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = {
				skip,
				take,
				requireTotalCount: true,
				...(sortSelector ? { sort: [{ selector: sortSelector, desc: sortDir === 'desc' }] } : {})
			};
			// `categories` is a required (non-nullable) field server-side — always send an array.
			const additionalFiltering: AdditionalFiltering = { categories: [], ...(filters ?? {}) };
			const res = (await contributorsApi.getAll({ loadOptions, additionalFiltering })).data;
			return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
		},
		placeholderData: keepPreviousData
	});
}

/** Single contributor by id (full record incl. grouplst). `create` → null. */
export function useContributor(id: number | null) {
	return useQuery<ContributorToken | null>({
		queryKey: contributorKeys.detail(id ?? 0),
		queryFn: async () => (id && id > 0 ? (await contributorsApi.getById(id)).data : null),
		enabled: id != null && id > 0
	});
}

export function useContributorMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: contributorKeys.all });
	const create = useMutation({
		// The controller returns Ok(created) with the new id, though the generated client types it loosely.
		mutationFn: async (form: ContributorFormValues) =>
			(await contributorsApi.insert(formToToken(form))).data as unknown as ContributorToken,
		onSuccess: invalidate
	});
	const update = useMutation({
		// Spread the original token first so server-owned fields not on the form survive the round-trip.
		mutationFn: async ({ form, original }: { form: ContributorFormValues; original: ContributorToken | null }) =>
			(await contributorsApi.update(formToToken(form, original))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await contributorsApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

/**
 * Upload a contributor image into slot `key` ("1".."5"). The contributor must already exist (id > 0);
 * the File is attached as form-data keyed by the slot subkey (mirrors the legacy
 * BinaryRepoImages/UploadContributor contract and the customer/manufacturer uploads).
 */
export async function uploadContributorImage(contributorId: number, key: string, file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadContributor(contributorId, key, { data: fd });
}
